import { Box, Button } from "@chakra-ui/react"
import { useContext, useMemo, useState } from "react"
import { AuthContext } from "contexts/AuthContext"
import { DataContext } from "contexts/DataContext"
import { useGroup } from "hooks/useGroup"
import { ActionDrawer } from "components/drawer"
import DataTableNew from "components/table/DataTableNew"
import MultiSelectFilter from "components/filter/MultiSelectFilter"
import { locationsByGroup } from "lib/membership"
import GroupModal from "views/modal/group"
import { columnsGroups } from "./columns/groups"

export default function GroupsPage() {
  const { user } = useContext(AuthContext)
  const [groups, loading, error, refresh] = useGroup({ user })
  // Locations come straight from the shared list; "Locations" column and its search use them
  const { locations } = useContext(DataContext)
  const [locationItems, , , refreshLocations] = locations

  // Chosen broadcasts; an empty list means "any broadcast"
  const [filter, setFilter] = useState({ broadcastIds: [] })

  // The same object while the locations do not change, so the table keeps its search texts
  const byGroup = useMemo(() => locationsByGroup(locationItems), [locationItems])
  const tableContext = useMemo(() => ({ byGroup, groups, refreshLocations }), [byGroup, groups, refreshLocations])

  const broadcastOptions = useMemo(() => {
    const seen = new Map()
    for (const group of groups) {
      if (group.broadcast && !seen.has(group.broadcast.id)) seen.set(group.broadcast.id, group.broadcast)
    }
    return [...seen.values()].map(br => ({ value: br.id, label: br.name }))
  }, [groups])

  const data = useMemo(() => groups.filter(group => {
    if (filter.broadcastIds.length > 0 && !filter.broadcastIds.includes(group.broadcastId)) return false
    return true
  }), [groups, filter])

  // Reloads the locations too: the "Locations" column comes from them
  const refreshAll = () => Promise.all([refresh(), refreshLocations()])

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} flex="1" minH={0} display="flex" flexDirection="column">
      <DataTableNew
        columns={columnsGroups}
        data={data}
        totalCount={groups.length}
        loading={loading}
        refresh={refreshAll}
        context={tableContext}
        resetPageKey={filter}
        defaultSort={[{ id: 'name', desc: false }]}
        filters={(
          <MultiSelectFilter label='Any broadcast'
            options={broadcastOptions}
            value={filter.broadcastIds}
            onChange={broadcastIds => setFilter({ ...filter, broadcastIds })} />
        )}
        actions={(
          <ActionDrawer
            title='Create group'
            size='2xl'
            trigger={
              <Button colorScheme='brand' variant='solid' size='sm' borderRadius={30} px='8'>
                New group
              </Button>
            }
          >
            <GroupModal onChange={refreshAll} />
          </ActionDrawer>
        )}
      />
    </Box>
  )
}
